import { useCallback, useEffect, useRef, useState } from 'react';

import {
  Stripe,
  Appearance,
  StripeElementLocale,
  StripeElementsOptionsMode,
} from '@stripe/stripe-js';

import { usePaymentState } from './use-payment-state';
import { useCheckout } from '../../../hooks/use-checkout';
import { getLocale } from '../../../utils/get-locale-util';
import { loadStripe } from '../../../utils/stripe-loader';
import { useAppConfig } from '../../../hooks/use-app-config';
import { EBEPaymentMethodsName, EPaymentProvider, PaymentMethod, PriceBreakdown } from '../../../contracts';
import { getStripeExpressCheckoutPK } from '../../../utils/get-stripe-express-checkout-pk';
import { getStripePaymentMethodTypesFromApm } from '../../../utils/get-stripe-payment-method-types-from-apm';
import { getPaymentKey } from '../../../utils/get-payment-key';

type StripeElementsPromise = PromiseLike<Stripe | null> | Stripe | null;

const REMOVE_FIELDS_LABELS_STYLING_RULES: Appearance = {
  rules: {
    '.Label': {
      opacity: '0',
      lineHeight: '0',
      marginBottom: '0',
    },
  },
};

const DEFAULT_STRIPE_OPTIONS: StripeElementsOptionsMode = {
  mode: 'payment',
  paymentMethodCreation: 'manual',
  paymentMethodTypes: [EBEPaymentMethodsName.card],
  loader: 'never',
  amount: 2000,
  currency: 'usd',
  locale: getLocale().pspLocale as StripeElementLocale,
  appearance: {
    variables: {
      fontSizeSm: '12px',
    },
    disableAnimations: true,
  },
};

const DEFAULT_EXPRESS_STRIPE_OPTIONS: StripeElementsOptionsMode = {
  mode: 'payment',
  paymentMethodCreation: 'manual',
  paymentMethodTypes: [EBEPaymentMethodsName.card],
  loader: 'never',
  amount: 2000,
  currency: 'usd',
  locale: getLocale().pspLocale as StripeElementLocale,
  appearance: {
    variables: {
      fontSizeSm: '12px',
    },
    disableAnimations: true,
  },
};

export function useStripeConfig() {
  const [paymentElementProviderKey, setPaymentElementProviderKey] = useState<string>('');
  const [paymentElementOptions, setPaymentElementOptions] = useState(DEFAULT_STRIPE_OPTIONS);
  const [expressElementOptions, setExpressElementOptions] = useState(
    DEFAULT_EXPRESS_STRIPE_OPTIONS
  );
  const [paymentElementPromise, setPaymentElementPromise] = useState<StripeElementsPromise>(null);
  const [expressElementPromise, setExpressElementPromise] = useState<StripeElementsPromise>(null);

  const { checkoutData, selectedPaymentMethod } = useCheckout();
  const { featureFlags } = useAppConfig();
  const priceBreakdown = usePaymentState((state) => state.priceBreakdown);

  const paymentMethods = checkoutData?.paymentMethods;
  const topLevelStripePk = checkoutData?.pks.stripe;
  const cardStripePk = paymentMethods?.find(
    (paymentMethod) =>
      paymentMethod.provider === EPaymentProvider.stripe &&
      paymentMethod.paymentMethodName === EBEPaymentMethodsName.card,
  )?.pspSubMidPk;
  const stripePK = getPaymentKey(cardStripePk, topLevelStripePk, {
    provider: EPaymentProvider.stripe,
    paymentMethod: EBEPaymentMethodsName.card,
  });
  const currency = checkoutData?.pricing.currencyCode.toLowerCase();

  const shouldHideFieldsLabels = featureFlags?.checkout_remove_field_labels;

  const lastPkRef = useRef<string | undefined>(undefined);
  const initializePKs = useCallback((paymentMethods: PaymentMethod[], stripePK: string) => {
    const expressCheckoutPK = getStripeExpressCheckoutPK(paymentMethods);
    if (lastPkRef.current !== stripePK) {
      setExpressElementPromise(loadStripe(expressCheckoutPK || stripePK));
      setPaymentElementPromise(loadStripe(stripePK));
      setPaymentElementProviderKey(stripePK);
      lastPkRef.current = stripePK;
    }
  }, []);

  const updatePrice = useCallback((priceBreakdown: PriceBreakdown, currency: string) => {
    const amount = priceBreakdown.priceTotal;
    setPaymentElementOptions((prevOptions) => {
      if (prevOptions.amount === amount && prevOptions.currency === currency) return prevOptions;
      return { ...prevOptions, amount, currency } as any;
    });
    setExpressElementOptions((prevOptions) => {
      if (prevOptions.amount === amount && prevOptions.currency === currency) return prevOptions;
      return { ...prevOptions, amount, currency } as any;
    });
  }, []);

  const changePaymentMethod = useCallback(
    (paymentMethod: PaymentMethod, topLevelPk: string | undefined) => {
      const paymentMethodTypes = getStripePaymentMethodTypesFromApm(paymentMethod);
      if (paymentMethodTypes.length) {
        setPaymentElementOptions((prevOptions) => ({
          ...prevOptions,
          paymentMethodTypes,
        }));
        const pk = getPaymentKey(paymentMethod.pspSubMidPk, topLevelPk, {
          provider: paymentMethod.provider,
          paymentMethod: paymentMethod.paymentMethodName,
        });
        if (pk) {
          setPaymentElementPromise(loadStripe(pk));
          setPaymentElementProviderKey(pk);
        }
      }
    },
    [],
  );

  useEffect(() => {
    if (shouldHideFieldsLabels) {
      setPaymentElementOptions((prevOptions) => ({
        ...prevOptions,
        appearance: { ...prevOptions.appearance, ...REMOVE_FIELDS_LABELS_STYLING_RULES },
      }));
    }
  }, [shouldHideFieldsLabels]);

  useEffect(() => {
    if (paymentMethods && stripePK) {
      initializePKs(paymentMethods, stripePK);
    }
  }, [paymentMethods, stripePK, initializePKs]);

  useEffect(() => {
    if (priceBreakdown && currency) {
      updatePrice(priceBreakdown, currency);
    }
  }, [priceBreakdown, currency]);

  useEffect(() => {
    if (selectedPaymentMethod) {
      changePaymentMethod(selectedPaymentMethod, topLevelStripePk);
    }
  }, [selectedPaymentMethod, paymentMethods, topLevelStripePk, changePaymentMethod]);

  return {
    paymentElementOptions,
    expressElementOptions,
    paymentElementPromise,
    expressElementPromise,
    paymentElementProviderKey,
  };
}
